// pattern: imperative-shell
// Camera/gallery photo capture → base64 for API upload
// Borrows queue pattern from Corkboard (George's wine scanner)

import type { ApiPhotoId } from "../shell/api-client";
import { api } from "../shell/api-client";

type IdentifyCallback = (result: ApiPhotoId) => void;
type StatusCallback = (message: string, isError?: boolean) => void;

export function renderPhotoCapture(
	container: HTMLElement,
	onIdentify: IdentifyCallback,
	onStatus: StatusCallback,
): void {
	container.innerHTML = `
		<div class="photo-section">
			<div class="photo-buttons">
				<button class="btn btn-secondary photo-btn" type="button" id="camera-btn">
					<span>\u{1F4F7}</span> Camera
				</button>
				<button class="btn btn-secondary photo-btn" type="button" id="gallery-btn">
					<span>\u{1F5BC}</span> Gallery
				</button>
			</div>
			<input type="file" id="camera-input" accept="image/*" capture="environment" style="display:none" />
			<input type="file" id="gallery-input" accept="image/*" style="display:none" />
			<div id="photo-preview" class="photo-preview"></div>
		</div>
	`;

	const cameraInput = container.querySelector<HTMLInputElement>("#camera-input")!;
	const galleryInput = container.querySelector<HTMLInputElement>("#gallery-input")!;
	const cameraBtn = container.querySelector<HTMLButtonElement>("#camera-btn")!;
	const galleryBtn = container.querySelector<HTMLButtonElement>("#gallery-btn")!;
	const previewEl = container.querySelector<HTMLElement>("#photo-preview")!;

	cameraBtn.addEventListener("click", () => cameraInput.click());
	galleryBtn.addEventListener("click", () => galleryInput.click());

	cameraInput.addEventListener("change", () => handleFile(cameraInput));
	galleryInput.addEventListener("change", () => handleFile(galleryInput));

	async function handleFile(input: HTMLInputElement) {
		const file = input.files?.[0];
		if (!file) return;

		// Reset for next use
		input.value = "";

		// Show preview
		const url = URL.createObjectURL(file);
		previewEl.innerHTML = `<img src="${url}" alt="Plant photo" />`;

		// Convert to base64
		onStatus("Identifying plant...");
		try {
			const base64 = await fileToBase64(file);
			const mimeType = file.type || "image/jpeg";
			const result = await api.identifyPhoto(base64, mimeType);

			const confidenceLabel =
				result.confidence === "high"
					? "confident"
					: result.confidence === "medium"
						? "fairly confident"
						: "uncertain";

			onStatus(
				`Identified: ${result.plant_name}${result.variety ? ` '${result.variety}'` : ""} (${confidenceLabel})${result.is_seed_packet ? " — seed packet" : ""}`,
			);
			onIdentify(result);
		} catch (e) {
			onStatus(e instanceof Error ? e.message : "Identification failed", true);
		}
	}
}

function fileToBase64(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const result = reader.result as string;
			// Strip the data URL prefix (data:image/jpeg;base64,...)
			const base64 = result.split(",")[1];
			if (base64) {
				resolve(base64);
			} else {
				reject(new Error("failed to read file as base64"));
			}
		};
		reader.onerror = () => reject(new Error("failed to read file"));
		reader.readAsDataURL(file);
	});
}
